import { el, append } from "../ui/dom";
import { signal, effect } from "../ui/signals";
import {
  pageHeader,
  prose,
  card,
  callout,
  codeBlock,
  stat,
  statRow,
  button,
} from "../ui/components";
import { plot, PALETTE } from "../ui/charts";
import { h2, legend, caption, ticker } from "./shared";
import { CuckooFilter } from "../core/cuckoo-filter";
import { mulberry32 } from "../core/rng";
import * as sizing from "../core/sizing";
import { formatBytes, formatPercent, formatOneIn } from "../ui/format";
import type { PageRenderer } from "../ui/router";

export const renderCuckoo: PageRenderer = (outlet) => {
  append(
    outlet,
    pageHeader({
      eyebrow: "Membership · with deletion",
      title: "Cuckoo filter",
      lede: "Approximate set membership like a Bloom filter — no false negatives — but you can also delete items, and it uses less space at low false-positive rates. Add and remove words and watch fingerprints shuffle between buckets.",
    }),
  );

  append(
    outlet,
    prose(
      el(
        "p",
        {},
        "Instead of setting bits, a cuckoo filter stores a short ",
        el("em", {}, "fingerprint"),
        " of each item in one of two candidate buckets. The clever part is how it finds the second bucket: ",
        el("code", {}, "i₂ = i₁ XOR hash(fingerprint)"),
        ". Because XOR is its own inverse, either bucket can compute the other from just the fingerprint — so when a bucket is full, an item can be ",
        el("strong", {}, "kicked"),
        " to its alternate, cuckoo-hashing style, without ever rehashing the original key.",
      ),
    ),
  );

  demoCard(outlet);
  compareCard(outlet);

  append(outlet, h2("Use it"));
  append(
    outlet,
    codeBlock(
      `import { CuckooFilter } from "./core";

const cf = CuckooFilter.create(1_000_000, 0.001); // capacity, target FPR
cf.add("session-abc");
cf.has("session-abc");    // → true
cf.delete("session-abc"); // ← Bloom filters can't do this
cf.has("session-abc");    // → false`,
    ),
  );

  append(
    outlet,
    callout(
      "warn",
      el(
        "div",
        {},
        el("strong", {}, "Deletion caveat. "),
        "Only delete items you actually inserted. Deleting a key you never added can remove a different item that happens to share the same fingerprint and bucket. Also, inserts can fail once the table is near full (~95%) — that's the signal to size up.",
      ),
    ),
  );
};

// ---------------------------------------------------------------------------

function demoCard(outlet: HTMLElement): void {
  const { tick, bump } = ticker();
  const testWord = signal("");
  const added = new Set<string>();
  // Small, legible filter: capacity 100 → 32 buckets of 4 slots.
  let cf = new CuckooFilter({ capacity: 100, bucketSize: 4, fingerprintBits: 8, seed: 3, rng: mulberry32(1) });

  function reset(): void {
    cf = new CuckooFilter({ capacity: 100, bucketSize: 4, fingerprintBits: 8, seed: 3, rng: mulberry32(1) });
    added.clear();
    bump();
  }

  function add(w: string): void {
    w = w.trim();
    if (!w || added.has(w)) return;
    if (cf.add(w)) added.add(w);
    bump();
  }
  function del(w: string): void {
    w = w.trim();
    if (!w) return;
    if (cf.delete(w)) added.delete(w);
    bump();
  }

  const bucketHost = el("div", { class: "cuckoo-grid" });
  const result = el("div", { class: "mini-note", style: { minHeight: "1.3em", marginTop: "6px" } });

  effect(() => {
    tick.value;
    const q = testWord.value.trim();
    const [c1, c2] = q ? cf.candidateBuckets(q) : [-1, -1];
    const fp = q ? cf.fingerprintOf(q) : -1;
    bucketHost.replaceChildren(
      ...Array.from({ length: cf.numBuckets }, (_, b) => {
        const isCand = b === c1 || b === c2;
        const box = el("div", { class: `bucket${isCand ? " cand" : ""}` }, el("span", { class: "bidx" }, String(b)));
        const slots = cf.bucket(b);
        for (const s of slots) {
          const match = q && s === fp && isCand;
          box.appendChild(
            el(
              "span",
              { class: `slot${s === 0 ? " empty" : ""}${match ? " match" : ""}` },
              s === 0 ? "·" : s.toString(16).padStart(2, "0"),
            ),
          );
        }
        return box;
      }),
    );

    if (!q) {
      result.textContent = "";
    } else {
      const present = cf.has(q);
      const truly = added.has(q);
      result.innerHTML = present
        ? truly
          ? `<span style="color:var(--accent)">“${esc(q)}” → <b>present</b> ✓ — fingerprint <code>${fp.toString(16)}</code> found in bucket ${c1} or ${c2}</span>`
          : `<span style="color:var(--warn)">“${esc(q)}” → <b>false positive</b> — its fingerprint collides with a stored one</span>`
        : `<span style="color:var(--text-dim)">“${esc(q)}” → <b>not present</b> — fingerprint <code>${fp.toString(16)}</code> is in neither candidate bucket</span>`;
    }
  });

  const controls = el(
    "div",
    { class: "lab-controls" },
    el(
      "label",
      { class: "control" },
      el("span", { class: "control-label" }, "Add / remove"),
      el("input", {
        class: "textinput",
        type: "text",
        placeholder: "type a word, press Enter to add",
        onkeydown: (e: KeyboardEvent) => {
          if (e.key === "Enter") {
            add((e.target as HTMLInputElement).value);
            (e.target as HTMLInputElement).value = "";
          }
        },
      }),
    ),
    el(
      "label",
      { class: "control" },
      el("span", { class: "control-label" }, "Test membership"),
      el("input", {
        class: "textinput",
        type: "text",
        placeholder: "highlight candidate buckets…",
        oninput: (e: Event) => (testWord.value = (e.target as HTMLInputElement).value),
      }),
    ),
    el(
      "div",
      { class: "button-row" },
      button({ label: "Add 20 random", variant: "ghost", onClick: () => { for (let i = 0; i < 20; i++) add(rand()); } }),
      button({ label: "Delete tested", variant: "ghost", onClick: () => del(testWord.value) }),
      button({ label: "Reset", variant: "ghost", onClick: reset }),
    ),
    statRow(
      stat({ label: "Items", value: () => (tick.value, String(cf.count)) }),
      stat({ label: "Load", accent: true, value: () => (tick.value, formatPercent(cf.loadFactor())) }),
    ),
    stat({ label: "False-positive rate", value: () => formatOneIn(cf.expectedFalsePositiveRate()), hint: "≈ 2·bucketSize / 2^fingerprintBits" }),
  );

  const viz = el(
    "div",
    {},
    el("div", { class: "viz-wrap" }, bucketHost),
    legend([
      ["var(--accent)", "stored fingerprint"],
      ["var(--accent-2)", "candidate bucket for tested word"],
    ]),
    result,
    caption("32 buckets × 4 slots. Each two-digit value is a stored fingerprint (hex). Test a word to light up its two candidate buckets."),
  );

  append(
    outlet,
    card({
      title: "Add, delete, and probe",
      subtitle: "Every item lives in one of two buckets. Deleting removes its fingerprint; membership checks just those two buckets.",
      children: [el("div", { class: "lab-layout" }, controls, viz)],
    }),
  );
}

// ---------------------------------------------------------------------------

function compareCard(outlet: HTMLElement): void {
  const n = 1_000_000;
  const bloom: Array<[number, number]> = [];
  const cuckoo: Array<[number, number]> = [];
  for (let e = 0.5; e <= 5.01; e += 0.1) {
    const p = 10 ** -e;
    bloom.push([p, sizing.bloomMemoryBytes(sizing.bloomBits(n, p))]);
    cuckoo.push([p, sizing.cuckooMemoryBytes(n, sizing.cuckooFingerprintBits(p))]);
  }

  const chart = plot({
    height: 300,
    xScale: "log",
    yScale: "log",
    xLabel: "target false-positive rate",
    yLabel: `memory for ${(n / 1e6).toFixed(0)}M items`,
    xFormat: (v) => formatPercent(v),
    yFormat: (v) => formatBytes(v),
    series: [
      { type: "line", points: bloom, color: PALETTE[4], label: "Bloom" },
      { type: "line", points: cuckoo, color: PALETTE[0], label: "Cuckoo" },
    ],
    legend: true,
  });

  append(
    outlet,
    card({
      title: "Cuckoo vs. Bloom, on memory",
      subtitle: "For the same false-positive rate, the cuckoo filter pulls ahead as the target FPR gets stricter — and it can delete.",
      children: [
        el("div", { class: "viz-wrap" }, chart),
        caption("Lower is better. The crossover is why storage systems that need deletion (and low FPR) favour cuckoo filters."),
      ],
    }),
  );
}

// ---------------------------------------------------------------------------

const NAMES = ["neo", "trinity", "morpheus", "cypher", "tank", "dozer", "switch", "apoc", "mouse", "oracle", "smith", "niobe"];
function rand(): string {
  return `${NAMES[Math.floor(Math.random() * NAMES.length)]}-${Math.floor(Math.random() * 10000)}`;
}
function esc(s: string): string {
  return s.replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" })[c]!);
}
